import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Trophy, Star, Flame, Zap, Target, Award, Crown,
  Dumbbell, Apple, Moon, Calendar, CalendarCheck,
  Users, Sparkles, Droplets, Heart, Battery,
  Lock, CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Icon mapping for badge icons
const iconMap = {
  'dumbbell': Dumbbell,
  'flame': Flame,
  'zap': Zap,
  'target': Target,
  'award': Award,
  'trophy': Trophy,
  'apple': Apple,
  'salad': Apple,
  'beef': Apple,
  'moon': Moon,
  'battery-full': Battery,
  'heart-pulse': Heart,
  'droplets': Droplets,
  'calendar': Calendar,
  'calendar-check': CalendarCheck,
  'star': Star,
  'crown': Crown,
  'users': Users,
  'sparkles': Sparkles,
};

const tierColors = {
  bronze: 'from-amber-700 to-amber-900',
  silver: 'from-slate-400 to-slate-600',
  gold: 'from-yellow-400 to-amber-500',
};

const tierBorders = {
  bronze: 'border-amber-700/50',
  silver: 'border-slate-400/50',
  gold: 'border-yellow-400/50',
};

const categoryLabels = {
  training: 'Training',
  nutrition: 'Nutrition',
  recovery: 'Recovery',
  milestone: 'Milestones',
  social: 'Social',
  special: 'Special',
};

const BadgesPage = () => {
  const { user } = useAuth();
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalPoints: 0, earnedCount: 0, totalCount: 0, progressPercent: 0 });
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [newlyEarned, setNewlyEarned] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');

  // Get score level based on points
  const getScoreLevel = (score) => {
    if (score >= 96) return { label: 'Elite', color: 'text-yellow-400', bgColor: 'bg-yellow-400/20' };
    if (score >= 86) return { label: 'Advanced', color: 'text-purple-400', bgColor: 'bg-purple-400/20' };
    if (score >= 71) return { label: 'Consistent', color: 'text-aura-success', bgColor: 'bg-aura-success/20' };
    if (score >= 51) return { label: 'Building', color: 'text-aura-secondary', bgColor: 'bg-aura-secondary/20' };
    return { label: 'Developing', color: 'text-aura-text-muted', bgColor: 'bg-white/10' };
  };

  // Calculate normalized score (0-100) for level display
  const getNormalizedScore = (points) => Math.min(100, Math.round((points / 1000) * 100));

  useEffect(() => {
    fetchBadges();
  }, []);

  // Show toast for newly earned badges
  useEffect(() => {
    if (newlyEarned.length > 0) {
      newlyEarned.forEach(badge => {
        toast.success(
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-400" />
            <span>Badge Earned: <strong>{badge.name}</strong></span>
          </div>,
          { duration: 5000 }
        );
      });
    }
  }, [newlyEarned]);

  const fetchBadges = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/badges`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      setBadges(response.data.badges || []);
      setStats({
        totalPoints: response.data.total_points || 0,
        earnedCount: response.data.earned_count || 0,
        totalCount: response.data.total_count || 0,
        progressPercent: response.data.progress_percent || 0
      });
      setNewlyEarned(response.data.newly_earned || []);
    } catch (error) {
      console.error('Failed to fetch badges:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...Object.keys(categoryLabels)];
  
  const filteredBadges = activeCategory === 'all' 
    ? badges 
    : badges.filter(b => b.category === activeCategory);

  const BadgeCard = ({ badge }) => {
    const IconComponent = iconMap[badge.icon] || Trophy;
    const isEarned = badge.earned;
    
    return (
      <div
        onClick={() => setSelectedBadge(badge)}
        className={`
          relative p-4 rounded-xl border cursor-pointer transition-all
          ${isEarned 
            ? `bg-gradient-to-br ${tierColors[badge.tier]} ${tierBorders[badge.tier]} hover:scale-105` 
            : 'bg-white/5 border-white/10 hover:border-white/20'
          }
        `}
        data-testid={`badge-${badge.id}`}
      >
        {/* Lock overlay for unearned */}
        {!isEarned && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl">
            <Lock className="w-6 h-6 text-white/50" />
          </div>
        )}
        
        <div className="flex flex-col items-center text-center gap-2">
          <div className={`
            w-12 h-12 rounded-full flex items-center justify-center
            ${isEarned ? 'bg-white/20' : 'bg-white/5'}
          `}>
            <IconComponent className={`w-6 h-6 ${isEarned ? 'text-white' : 'text-white/30'}`} />
          </div>
          <div>
            <p className={`font-heading font-bold text-sm uppercase tracking-tight ${isEarned ? 'text-white' : 'text-white/50'}`}>
              {badge.name}
            </p>
            <p className={`text-xs ${isEarned ? 'text-white/70' : 'text-white/30'}`}>
              {badge.points > 0 ? `${badge.points} pts` : ''}
            </p>
          </div>
        </div>
        
        {/* Earned checkmark */}
        {isEarned && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-aura-success rounded-full flex items-center justify-center">
            <CheckCircle className="w-3 h-3 text-white" />
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout title="Badges">
        <div className="px-4 py-6 max-w-7xl mx-auto">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="h-32 bg-white/5 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Aura Performance Score">
      <div className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Section Header */}
        <div className="mb-2">
          <p className="text-aura-text-muted text-sm">Consistency drives your score.</p>
        </div>
        
        {/* Stats Header */}
        <Card className="bg-gradient-to-r from-aura-primary/20 to-aura-secondary/20 border-white/10">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center">
                  <Trophy className="w-8 h-8 text-white" />
                </div>
                <div>
                  <p className="text-xs text-aura-text-muted uppercase tracking-wider">Aura Performance Score</p>
                  <p className="font-heading font-bold text-4xl text-white">{stats.totalPoints}</p>
                  <div className={`inline-flex items-center gap-2 mt-1 px-2 py-0.5 rounded-full ${getScoreLevel(getNormalizedScore(stats.totalPoints)).bgColor}`}>
                    <span className={`text-sm font-medium ${getScoreLevel(getNormalizedScore(stats.totalPoints)).color}`}>
                      {getScoreLevel(getNormalizedScore(stats.totalPoints)).label}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex-1 max-w-md w-full">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-aura-text-muted">Badge Progress</span>
                  <span className="text-white font-medium">{stats.earnedCount}/{stats.totalCount}</span>
                </div>
                <Progress value={stats.progressPercent} className="h-3 bg-white/10" />
                <p className="text-xs text-aura-text-muted mt-2">Elite athletes maintain 85+ • Your habits define your performance</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Category Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`
                px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors
                ${activeCategory === cat 
                  ? 'bg-aura-primary text-white' 
                  : 'bg-white/5 text-aura-text-muted hover:bg-white/10 hover:text-white'
                }
              `}
              data-testid={`category-${cat}`}
            >
              {cat === 'all' ? 'All Badges' : categoryLabels[cat]}
            </button>
          ))}
        </div>

        {/* Badges Grid */}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
          {filteredBadges.map(badge => (
            <BadgeCard key={badge.id} badge={badge} />
          ))}
        </div>

        {filteredBadges.length === 0 && (
          <div className="text-center py-12">
            <Trophy className="w-12 h-12 text-aura-text-muted mx-auto mb-4" />
            <p className="text-white font-medium mb-2">Your score builds through consistency</p>
            <p className="text-aura-text-muted">Start your system to earn badges.</p>
          </div>
        )}

        {/* Badge Detail Dialog */}
        <Dialog open={!!selectedBadge} onOpenChange={() => setSelectedBadge(null)}>
          <DialogContent className="bg-aura-surface border-white/10 text-white max-w-sm">
            {selectedBadge && (
              <>
                <div className={`
                  mx-auto w-24 h-24 rounded-full flex items-center justify-center -mt-16
                  ${selectedBadge.earned 
                    ? `bg-gradient-to-br ${tierColors[selectedBadge.tier]}` 
                    : 'bg-white/10'
                  }
                `}>
                  {React.createElement(iconMap[selectedBadge.icon] || Trophy, {
                    className: `w-12 h-12 ${selectedBadge.earned ? 'text-white' : 'text-white/30'}`
                  })}
                </div>
                
                <DialogHeader className="text-center pt-2">
                  <DialogTitle className="font-heading text-2xl uppercase tracking-tight">
                    {selectedBadge.name}
                  </DialogTitle>
                  <div className="flex justify-center gap-2 mt-2">
                    <Badge variant="outline" className={`border-${selectedBadge.tier === 'gold' ? 'yellow' : selectedBadge.tier === 'silver' ? 'slate' : 'amber'}-500 text-white capitalize`}>
                      {selectedBadge.tier}
                    </Badge>
                    <Badge variant="outline" className="border-white/30 text-white">
                      {categoryLabels[selectedBadge.category]}
                    </Badge>
                  </div>
                </DialogHeader>
                
                <div className="text-center space-y-4 pt-4">
                  <p className="text-aura-text-muted">{selectedBadge.description}</p>
                  
                  {selectedBadge.points > 0 && (
                    <div className="flex items-center justify-center gap-2">
                      <Star className="w-5 h-5 text-yellow-400" />
                      <span className="font-heading font-bold text-xl text-white">{selectedBadge.points} Points</span>
                    </div>
                  )}
                  
                  {selectedBadge.earned ? (
                    <div className="bg-aura-success/20 rounded-lg p-3">
                      <p className="text-sm text-aura-success flex items-center justify-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Earned on {new Date(selectedBadge.earned_at).toLocaleDateString()}
                      </p>
                    </div>
                  ) : (
                    <div className="bg-white/5 rounded-lg p-3">
                      <p className="text-sm text-aura-text-muted flex items-center justify-center gap-2">
                        <Lock className="w-4 h-4" />
                        Not yet earned
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default BadgesPage;
